import ImageDb from '@/stores/imageDB'
import { useImageStore } from '@/stores/imageStore'
import { getActivePinia } from 'pinia'
import { ref } from 'vue'
import { NO_QUEUE_HEADER, forgetQueuedCopies } from '@/helpers/offlineSupport.js'
import { sessionEnded } from '@/helpers/sessionState.js'

import { ModelRegistry } from '@/models/modelRegistry.js'

export { NO_QUEUE_HEADER }
// How long a save may sit in the service worker's queue, while online, before this page sends it too
const STALE_QUEUE_MS = 2 * 60 * 1000
// How often a save the server refused is tried again
const RETRY_REFUSED_MS = 2 * 60 * 1000

// Strictly increasing times, shared by every store on this page: a save's revision, and when each of
// its requests was sent. A request sent before a record's latest save carries an older version of it.
let lastStamp = 0
function stamp() {
  lastStamp = Math.max(Date.now(), lastStamp + 1)
  return lastStamp
}

export const useSyncableModel = (modelResource, options = {}) => {
  const imageStore = useImageStore()
  const baseOptions = { ..._defaultOptions(), ...options }

  let collection = ref([])
  // Local saves the server may not have yet, one entry per record: { uuid, isNew, queued, partnerId,
  // revision, queuedAt, lastAttemptAt, lastError, bypassQueue, stashed }.
  // `queued` says who will deliver it -- true: the service worker queued the request and replays it;
  // false: nobody did (no service worker was in control, so `fetch` itself failed), and
  // `resendUnsent` has to. `isNew` stays true until a send succeeds, because a record that never
  // reached the server has to be created there, not updated.
  // While an entry exists, the local record is newer than anything the server holds for it, so
  // records coming from the server do not replace it.
  // `partnerId` is the partner the save was made for. It is sent only while that partner is selected,
  // because the agent's key then points at it; under another partner the record waits in `stashed`.
  let pendingSaves = ref([])
  // `revision` is when the latest save was made. Only the delivery of a request sent since then
  // settles the entry (recordDelivered); an older one carried an older version of the record.
  // The records this page is sending right now (uuid -> when the latest send started), which
  // resendUnsent leaves to that send
  const sending = new Map()
  // The time of the save request about to go out, per record, for the request's Offline-Meta
  const requestStamps = new Map()
  const className = baseOptions.className
  const paramKey = baseOptions.paramKey
  const currentPartnerId = baseOptions.currentPartnerId
  // Fields the app only reads, which a save leaves out so the server's copy is never overwritten by
  // whatever the phone was last sent. The local record keeps them.
  const unsentFields = baseOptions.unsentFields

  _trackModelRequestMetadata()

  function _defaultOptions() {
    return {
      currentPartnerId: () => getActivePinia()?._s?.get('userStore')?.partner?.id ?? null,
      unsentFields: []
    }
  }

  function _trackModelRequestMetadata() {
    modelResource.use((url, options, body) => {
      let uuid = body?.[paramKey]?.uuid;

      // The partner goes with a request the service worker queues: it replays the request only for
      // that partner. A save (sent by _send) also carries when it was sent, which orders it against
      // the record's other saves: the worker drops an older queued copy, and reports which version it
      // delivered. Any other request about the record (an action such as confirm_payment) is marked as
      // not a save, so it is never taken for one.
      if (uuid) {
        const sentAt = requestStamps.get(uuid)
        requestStamps.delete(uuid)

        options.headers["Offline-Meta"] = JSON.stringify({
          linked_model_type: className, linked_model_uuid: uuid, partner_id: currentPartnerId(),
          save: sentAt != null, ...(sentAt != null ? { sent_at: sentAt } : {})
        })
      }

      return options
    })
  }

  async function getCollection() {
    let response = await modelResource.get("/")
    // Records for a session that ended while they were on their way are not kept
    if (sessionEnded()) return

    const serverRecords = response.data.map((item) => {
      if (Object.hasOwn(item, 'image_urls') && item.image_urls.length > 0) {
        ImageDb.addUrlFromServer(item.image_urls[0]).then((uuid) => {
          imageStore.collection.push({ type: paramKey, type_id: item.uuid, uuid: uuid })
        })
      } else if (Object.hasOwn(item, 'documents') && item.documents.length > 0) {
        ImageDb.addUrlFromServer(item.documents.at(-1).url).then((uuid) => {
          imageStore.collection.push({ type: paramKey, type_id: item.uuid, uuid: uuid, })
        })
      }

      return { ...item, synced: true }
    })
    const serverUuids = new Set(serverRecords.map((record) => record.uuid))

    // Work saved on this device that the server does not have yet survives the reload: a record
    // with a pending save for the partner selected, and any unsynced record the server has never
    // heard of. A record with a pending save for another partner leaves the collection with its entry
    // (`stashed`), so it is neither shown nor sent here, and returns when that partner is selected.
    for (const entry of pendingSaves.value) {
      const record = getLocalRecord(entry.uuid)
      if (record && !_isForCurrentPartner(entry)) entry.stashed = { ...record }
    }

    const keptLocal = collection.value.filter((record) => {
      if (record.synced) return false
      const entry = _pendingEntry(record.uuid)
      return entry ? _isForCurrentPartner(entry) : !serverUuids.has(record.uuid)
    })
    const keptUuids = new Set(keptLocal.map((record) => record.uuid))

    for (const entry of pendingSaves.value) {
      if (!entry.stashed || !_isForCurrentPartner(entry)) continue
      if (!keptUuids.has(entry.uuid)) keptLocal.push(entry.stashed)
      keptUuids.add(entry.uuid)
      delete entry.stashed
    }

    collection.value = serverRecords.filter((record) => !keptUuids.has(record.uuid)).concat(keptLocal)
    _dropPendingWithoutRecord()
  }

  // Applies records sent by the server, except over a local record with a pending save.
  function mergeFromServer(records) {
    update(records.filter((record) => !_isPending(record.uuid)), { synced: true })
    _dropPendingWithoutRecord()
  }

  // Takes the records the server says were deleted off this device, with any save still waiting for
  // them, including one stashed for another partner: the server ignores a save of a deleted record.
  // A uuid this device does not hold is ignored.
  function removeDeleted(uuids) {
    const deleted = new Set(uuids)
    if (deleted.size === 0) return

    collection.value = collection.value.filter((record) => !deleted.has(record.uuid))
    pendingSaves.value = pendingSaves.value.filter((entry) => !deleted.has(entry.uuid))
  }

  function find(uuid) {
    const obj = collection.value.find((item) => item.uuid === uuid)
    return Object.assign(modelTemplate(), obj)
  }

  // The service worker delivered a save of the record, sent at `sentAt`. It settles the record only if
  // it was sent since the latest save; otherwise a newer version is still to be delivered. A request
  // from before saves were stamped (no `sentAt`) settles only an entry from before then too.
  function recordDelivered(uuid, sentAt = null) {
    const entry = _pendingEntry(uuid)
    if (entry && !_isCurrentDelivery(entry, sentAt)) return false

    setSynced(uuid)
    return true
  }

  function _isCurrentDelivery(entry, sentAt) {
    if (entry.revision == null) return true
    return sentAt != null && sentAt >= entry.revision
  }

  function setSynced(uuid) {
    _clearPending(uuid)

    let record = getLocalRecord(uuid)
    if (!record) return

    update([record], { synced: true })
  }

  // Saves locally first, then tries the server. Never throws for a missing network: the record is
  // then left unsynced on this device and the response is `{ error: "failed" }`.
  async function save(obj) {
    const isNew = getLocalRecord(obj.uuid) == null || _pendingEntry(obj.uuid)?.isNew === true

    // Update the local store, but set the record as unsynced.
    // We do this so that in case the remote call fails, we still have the local changes saved
    update([obj], { synced: false })
    // `queued: null` until this send settles who delivers it
    _setPending(obj.uuid, { isNew, queued: null, partnerId: currentPartnerId(), revision: stamp() })

    return _send(obj, isNew)
  }

  // `bypassQueue` asks the service worker to pass a failure straight back rather than queue the
  // request: this page is already resending it, and a second queued copy would pile up.
  async function _send(obj, isNew, { bypassQueue = false } = {}) {
    let requestBody = { [paramKey]: _withoutUnsentFields(obj) }
    let endpoint = isNew ? "/" : `${obj.uuid}`
    let httpMethod = isNew ? modelResource.post : modelResource.updateMethod()
    let headers = bypassQueue ? { [NO_QUEUE_HEADER]: '1' } : {}
    let response
    const startedAt = stamp()

    _setPending(obj.uuid, { lastAttemptAt: startedAt })
    sending.set(obj.uuid, startedAt)

    try {
      // Read by the Offline-Meta middleware, which runs before the request's first await
      requestStamps.set(obj.uuid, startedAt)
      response = await httpMethod(endpoint, requestBody, headers)
    }
    catch (error) {
      // No usable response at all. Offline, that means no service worker was in control to queue the
      // request, so nothing else will ever send it: resendUnsent does.
      console.warn(`Could not send ${className} ${obj.uuid}; it stays on this device until it can be sent`, error)
      if (_latestSend(obj.uuid, startedAt)) _setPending(obj.uuid, { queued: false, lastError: _unreachable() })
      return { error: "failed", queued: false }
    }
    finally {
      if (sending.get(obj.uuid) === startedAt) sending.delete(obj.uuid)
    }

    // The session ended while this was out: its outcome belongs to nobody now
    if (sessionEnded()) return response

    // A newer save of the record started while this one was out: its own send settles the entry,
    // and this older copy must not overwrite the newer local record
    if (!_latestSend(obj.uuid, startedAt)) return response

    if (response?.error === "failed") {
      // The service worker answered in the server's place: whether it queued the request (one that
      // predates the `queued` flag always queued while signed in), and the server's status when the
      // server did answer -- the one clue to why a save never arrives.
      const queued = response.queued !== false
      const lastError = response.status ? `HTTP ${response.status}` : _unreachable()
      if (response.status) console.warn(`The server refused ${className} ${obj.uuid} (${lastError}); it will be sent again`)

      _setPending(obj.uuid, { queued, lastError, ...(queued && !_pendingEntry(obj.uuid)?.queued ? { queuedAt: Date.now() } : {}) })
    }
    else {
      // Now that the local store has synced with the remote server, we can set the local value as synced.
      // A record taken off the device while this was out (the server deleted it) is not brought back.
      if (getLocalRecord(obj.uuid)) update([obj], { synced: true })
      _clearPending(obj.uuid)
      // The service worker may hold an older copy of this save (one queued before, or one this page
      // stopped waiting for), which must not be replayed over it
      forgetQueuedCopies([{ uuid: obj.uuid, before: startedAt }])
    }

    return response
  }

  function _withoutUnsentFields(obj) {
    if (unsentFields.length === 0) return obj

    const body = { ...obj }
    unsentFields.forEach((field) => delete body[field])
    return body
  }

  // Sends the records whose last save nobody queued, including one whose send never finished (the
  // page closed mid-save, so `queued` was never settled). Stops at the first one that still cannot
  // be sent, because the rest would fail the same way. One the server refused is tried again only
  // every RETRY_REFUSED_MS, rather than on every sync.
  async function resendUnsent({ now = Date.now() } = {}) {
    for (const entry of pendingSaves.value.filter((pending) => pending.queued !== true)) {
      // Settled since the loop started, being sent right now, or another partner's
      if (!_pendingEntry(entry.uuid) || sending.has(entry.uuid) || !_isForCurrentPartner(entry)) continue

      const record = getLocalRecord(entry.uuid)
      if (!record) {
        if (!entry.stashed) _clearPending(entry.uuid)
        continue
      }
      if (entry.lastError?.startsWith('HTTP') && now - (entry.lastAttemptAt || 0) < RETRY_REFUSED_MS) continue

      const response = await _send(record, entry.isNew, { bypassQueue: entry.bypassQueue === true })
      if (response?.error === "failed" && response.queued === false && !response.status) break
    }
  }

  // `queuedUuids` = the records the service worker still has requests to deliver for; `delivered` =
  // the ones it has delivered, each with when the latest delivered save was sent (see
  // recordDelivered); `unqueuedUuids` = the ones it gave up on (the server did not know the
  // device) and hands back. A save handed back falls to resendUnsent, and so does one the worker
  // neither holds nor delivered: it lost it (stopped mid-run). When no service worker can run at all,
  // its saves fall to resendUnsent too. `asOf` is when the worker was asked: a save it queued after
  // that is not in its answer, and is left for the next one.
  //
  // A save the worker still holds keeps the worker's reason (`reasons`) for its last failed attempt,
  // and is released to this page once it has waited too long (see releaseStaleQueued).
  //
  // The previous version of the app kept no entries, so a save it queued that the worker hands back
  // would be lost: one queued from this partner's device (`adoptable`) and still unsynced here is
  // taken in and sent.
  //
  // Returns the handed-back uuids this store has taken: only those may the worker forget.
  function reconcileQueued(queuedUuids, unqueuedUuids = new Set(), {
    now = Date.now(), asOf = Infinity, reasons = new Map(), delivered = new Map(), adoptable = new Set(), onlineSince = 0
  } = {}) {
    const taken = new Set()

    for (const uuid of adoptable) {
      const record = getLocalRecord(uuid)
      if (unqueuedUuids.has(uuid) && !_pendingEntry(uuid) && record && !record.synced) {
        _setPending(uuid, { isNew: true, queued: false, partnerId: currentPartnerId() })
        taken.add(uuid)
      }
    }

    for (const entry of [...pendingSaves.value]) {
      if (unqueuedUuids.has(entry.uuid)) taken.add(entry.uuid)
      if (entry.queued !== true || entry.queuedAt > asOf) continue

      if (queuedUuids == null || unqueuedUuids.has(entry.uuid)) _setPending(entry.uuid, { queued: false })
      else if (queuedUuids.has(entry.uuid)) {
        if (reasons.has(entry.uuid)) _setPending(entry.uuid, { lastError: reasons.get(entry.uuid) })
        _releaseIfStale(entry, { now, onlineSince })
      }
      // Not held, and its latest version not delivered: the worker lost it (stopped mid-run)
      else if (!(delivered.has(entry.uuid) && recordDelivered(entry.uuid, delivered.get(entry.uuid) || null))) {
        _setPending(entry.uuid, { queued: false })
      }
    }

    return taken
  }

  // A save the worker has held for longer than STALE_QUEUE_MS since this device was last back online
  // is sent by this page as well, so a worker whose replays keep failing (a sync it cannot register,
  // a server error) cannot hold the agent's work back indefinitely. Counting from when the device came
  // online gives the worker its turn first: it replays when the signal returns, and a save it is
  // replaying anyway should not be sent twice. When the page does send it, the worker is told to drop
  // its copy (forgetQueuedCopies). Also used on its own when the worker cannot say what it holds.
  function releaseStaleQueued({ now = Date.now(), onlineSince = 0 } = {}) {
    for (const entry of [...pendingSaves.value]) {
      if (entry.queued === true) _releaseIfStale(entry, { now, onlineSince })
    }
  }

  function _releaseIfStale(entry, { now, onlineSince }) {
    // Queued by a version of the app that did not note when: it starts waiting now
    if (!entry.queuedAt) _setPending(entry.uuid, { queuedAt: now })
    if (!deviceOnline()) return

    if (now - Math.max(entry.queuedAt, onlineSince) > STALE_QUEUE_MS) _setPending(entry.uuid, { queued: false, bypassQueue: true })
  }

  // Sends a record's pending save from this page now, whoever was to deliver it, because the agent
  // asked. Resolves with the entry left when it still did not get through (its lastError says why),
  // or null once the server has it.
  async function sendNow(uuid) {
    const entry = _pendingEntry(uuid)
    const record = getLocalRecord(uuid)
    if (!entry || !record || !_isForCurrentPartner(entry)) return entry
    if (sending.has(uuid)) return entry

    _setPending(uuid, { queued: false, bypassQueue: true })
    await _send(record, entry.isNew, { bypassQueue: true })
    return _pendingEntry(uuid)
  }

  function deviceOnline() {
    return typeof navigator === 'undefined' || navigator.onLine !== false
  }

  // Why a send got no answer: 'offline' when the device has no network, else 'network' -- it has one,
  // but the server could not be reached
  function _unreachable() {
    return deviceOnline() ? 'network' : 'offline'
  }

  function pendingSaveFor(uuid) {
    return _pendingEntry(uuid)
  }

  function _pendingEntry(uuid) {
    return pendingSaves.value.find((entry) => entry.uuid === uuid) || null
  }

  function _latestSend(uuid, startedAt) {
    const entry = _pendingEntry(uuid)
    return entry == null || entry.lastAttemptAt === startedAt
  }

  function _isPending(uuid) {
    return _pendingEntry(uuid) != null && getLocalRecord(uuid) != null
  }

  // An entry with no partner (made before entries had one), or with no partner selected, counts as
  // the current partner's
  function _isForCurrentPartner(entry) {
    const partnerId = currentPartnerId()
    return entry.partnerId == null || partnerId == null || entry.partnerId === partnerId
  }

  // Creates or updates the entry; `isNew` only ever turns on, so a record that never reached the
  // server is still created there however many times it was edited in the meantime. Other details
  // (queued, partnerId, queuedAt, lastAttemptAt, lastError, bypassQueue) change only when given.
  function _setPending(uuid, { isNew = false, ...details } = {}) {
    const entry = _pendingEntry(uuid)

    if (entry) {
      entry.isNew = entry.isNew || isNew
      Object.assign(entry, details)
    }
    else pendingSaves.value.push({ uuid, isNew, queued: null, ...details })
  }

  function _clearPending(uuid) {
    pendingSaves.value = pendingSaves.value.filter((entry) => entry.uuid !== uuid)
  }

  // A record removed from this device (deleted, or a store reset on sign-out) has nothing left to send.
  // One stashed for another partner is still waiting.
  function _dropPendingWithoutRecord() {
    pendingSaves.value = pendingSaves.value.filter((entry) => entry.stashed || getLocalRecord(entry.uuid) != null)
  }

  function update(updates, { synced = true } = {}) {
    updates.forEach((updateItem) => {
      if (getLocalRecord(updateItem.uuid)) {
        collection.value.splice(collection.value.findIndex((item) => item.uuid == updateItem.uuid),
          1,
          { ...updateItem, synced: synced }
        )
      }
      else {
        collection.value.push({ ...updateItem, synced: synced })
      }
    })
  }

  async function remove(obj) {
    if (getLocalRecord(obj.uuid)) {
      let index = collection.value.findIndex((item) => item.uuid == obj.uuid)
      collection.value.splice(index, 1)
      _clearPending(obj.uuid)
      return modelResource.performDestroy(obj.uuid)
    }
  }

  function findByCustomerUuid(uuid) {
    let item = collection.value.find((e) => e.customer_uuid === uuid)
    item ??= { customer_uuid: uuid }
    return item
  }

  function findAllByCustomerUuid(uuid) {
    return collection.value.filter((e) => e.customer_uuid === uuid)
  }

  function setAllSynced() {
    collection.value = collection.value.map((item) => {
      return { ...item, synced: true }
    })
  }

  // Includes the records waiting for another partner, so signing out still warns about them
  function unsyncedCount() {
    return collection.value.filter((item) => !item.synced).length + pendingSaves.value.filter((entry) => entry.stashed).length
  }

  function all() {
    // This should be a clone we want reactivity
    return collection.value.map((item) => {
      return Object.assign(modelTemplate(), item)
    })
  }

  function getLocalRecord(uuid) {
    return collection.value.find((item) => item.uuid === uuid) || null
  }

  function getRecord(uuid) {
    let record

    if (uuid == null) return Promise.resolve(null)

    // Search in-memory cache
    record = getLocalRecord(uuid)
    if (record) return Promise.resolve(record)

    // Fallback to network request
    return modelResource.get(`${uuid}`)
    .then((data) => {
      update([data.data || {}])
      // Search for the item again. We do this because the `update` method may modify the data
      return getLocalRecord(uuid)
    })
  }

  function isNewRecord(uuid) {
    return getLocalRecord(uuid) == null
  }

  function modelTemplate() {
    let model = ModelRegistry.find(className)
    return new model()
  }

  return {
    collection,
    pendingSaves,
    paramKey,
    resource: modelResource,
    getCollection,
    mergeFromServer,
    removeDeleted,
    resendUnsent,
    reconcileQueued,
    releaseStaleQueued,
    sendNow,
    pendingSaveFor,
    find,
    setSynced,
    recordDelivered,
    save,
    update,
    delete: remove,
    findByCustomerUuid,
    findAllByCustomerUuid,
    setAllSynced,
    unsyncedCount,
    all,
    getRecord,
    isNewRecord,
  }
}
